import type { TeamSearchSort } from "./team-search-sort.models";
import z from "zod";

import type { AuthTeamAccessMode, ServiceResponse } from "./BaseService";
import type { User } from "./user.models";
import { TeamTypeSchema } from "../schema/TeamTypeSchema";
import { USER_REGISTRATION_METHOD_SCHEMA } from "../schema/UserRegistrationMethodSchema";
import { serviceRequest } from "./BaseService";
import { OAuthProvider } from "./oauth.models";
import { authenticationResponse } from "./user.models";

/** ******************************************************************************
 *  Constants
 ******************************************************************************* */

/**
 * A team's egress allowlist (`teams.allowed_downstream_providers`): the
 * actual catalog provider identities the team is permitted to reach, plus
 * supported legacy aliases such as `gemini` for `google`.
 *
 * Non-empty by construction. An empty allowlist would deny every provider and
 * black-hole the team's traffic, so it is rejected here at the write boundary
 * rather than being stored and interpreted as "allow nothing" — absence of a
 * restriction is `null`, never `[]`.
 *
 * Values match the resolved provider identity after alias normalization. The
 * inference origin is environment-specific (`inference-net` in production,
 * `inference-cool` in development, `inference-local` locally), so an
 * allowlist is written for the environment it runs in.
 */
export const TeamProviderAllowlistSchema = z.array(z.string()).nonempty();
export type TeamProviderAllowlist = z.infer<typeof TeamProviderAllowlistSchema>;

// Teams are provided an initial balance of 1 dollar for free on registration.
export const INITIAL_TEAM_REGISTRATION_TOKEN_ALLOWANCE_USD =
  process.env.NODE_ENV === "local" ? 10_000 : 1;

export const REQUIRE_PAYMENT_METHOD_FOR_ONBOARDING_ENV =
  "REQUIRE_PAYMENT_METHOD_FOR_ONBOARDING";

/** Config injected into TeamService controlling onboarding's payment-method policy. */
export type TeamServiceConfig = {
  paymentMethodRequiredForOnboarding: boolean;
};

/**
 * Resolve REQUIRE_PAYMENT_METHOD_FOR_ONBOARDING. Absent/empty defaults to
 * required=true (preserves the always-required behavior); explicit "false"
 * relaxes it so onboarding stamps payment_onboarding_exempt (INF-4765).
 */
export function resolvePaymentOnboardingConfig(env: {
  required?: string;
}): TeamServiceConfig {
  const raw = env.required?.trim().toLowerCase();
  if (raw === "false") return { paymentMethodRequiredForOnboarding: false };
  if (raw == null || raw === "" || raw === "true") {
    return { paymentMethodRequiredForOnboarding: true };
  }
  throw new Error(
    `${REQUIRE_PAYMENT_METHOD_FOR_ONBOARDING_ENV} must be true or false`,
  );
}

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

export const TeamSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDisabled: z.boolean(),
  waitlistedAt: z.date().nullish(),
  onboardedAt: z.date().nullish(),
  paymentOnboardingExempt: z.boolean(),
  claimedPhoneNumber: z.string().nullish(),
  integratedAt: z.date().nullish(),
  retainInferenceResults: z.boolean(),
  // Plain array here, not TeamProviderAllowlistSchema: this mirrors the stored
  // row, and an empty array is bad data that must still read back rather than
  // failing the whole team parse. The non-empty guarantee is enforced where
  // the allowlist is written and normalized where it is read for enforcement.
  allowedDownstreamProviders: z.array(z.string()).nullish(),
  type: z.enum(TeamTypeSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
  stripeCustomerId: z.string().nullish(),
});

export type Team = z.infer<typeof TeamSchema>;

export type ClaimVerifiedPhoneNumberResult =
  | { claimed: true }
  | {
      claimed: false;
      reason: "already_claimed_by_this_team" | "claimed_by_other_team";
    };

export type ApplySuccessfulSmsVerificationResult = {
  claimed: boolean;
  granted: boolean;
  alreadyVerified: boolean;
};

export const SMS_VERIFICATION_CREDIT_REASON = "sms_verification_credit";
export const SMS_VERIFICATION_CREDIT_EXTERNAL_ID_PREFIX = "sms-verification";

export function buildSmsVerificationCreditExternalId({
  teamId,
}: {
  teamId: string;
}): string {
  return `${SMS_VERIFICATION_CREDIT_EXTERNAL_ID_PREFIX}:${teamId}`;
}

export const ENABLE_SMS_VERIFICATION_CREDIT_ENV =
  "ENABLE_SMS_VERIFICATION_CREDIT";
export const SMS_VERIFICATION_CREDIT_AMOUNT_CENTS_ENV =
  "SMS_VERIFICATION_CREDIT_AMOUNT_CENTS";

export type SmsVerificationCreditConfig = {
  enabled: boolean;
  amountCents: number;
};

/**
 * Parse the SMS-verification credit flag and required amount. Both keys
 * must be present: enabled is `true`/`false`, amount is a positive integer.
 */
export function resolveSmsVerificationCreditConfig(env: {
  enabled?: string;
  amountCents?: string;
}): SmsVerificationCreditConfig {
  const enabledRaw = env.enabled?.trim().toLowerCase();
  if (enabledRaw !== "true" && enabledRaw !== "false") {
    throw new Error(
      `${ENABLE_SMS_VERIFICATION_CREDIT_ENV} must be true or false`,
    );
  }
  const amountCents = Number(env.amountCents);
  if (!Number.isInteger(amountCents) || amountCents <= 0) {
    throw new Error(
      `${SMS_VERIFICATION_CREDIT_AMOUNT_CENTS_ENV} must be a positive integer`,
    );
  }
  return { enabled: enabledRaw === "true", amountCents };
}

export type OnboardingCompletionDecision =
  | { kind: "blocked" }
  | { kind: "complete"; stampPaymentExempt: boolean };

/**
 * Pure decision for onboarding completion.
 *
 * Payment no longer blocks exiting onboarding (INF-4858). A team that
 * finishes without a card is stamped payment-exempt so the dashboard
 * payment gate cannot bounce them to `/onboarding/billing`.
 */
export function decideOnboardingCompletion({
  paymentMethodRequired,
  billingCompletionReady,
}: {
  paymentMethodRequired: boolean;
  billingCompletionReady: boolean;
}): OnboardingCompletionDecision {
  return {
    kind: "complete",
    stampPaymentExempt: !paymentMethodRequired || !billingCompletionReady,
  };
}

/**
 * A team's spendable credit balance (`team.getCreditBalance`).
 * `balanceNanocredits` is a string, not a number: the observability-api tRPC has
 * no superjson transformer, so the underlying bigint is serialized as a decimal
 * string to cross the wire without precision loss.
 */
export const teamCreditBalanceSchema = z.object({
  teamId: z.string(),
  balanceNanocredits: z.string(),
});

/**
 * A team's usage + limits across every metered surface (`team.getUsageStats`).
 * Limit fields are `number | null` (null = unbounded, 0 = none allowed, N = cap).
 * The two halo inference-spend fields are decimal STRINGS: they are nanocredit
 * bigints in the service, serialized as strings to cross this transformer-less
 * tRPC (the resolver stringifies them). `tier` mirrors `SUBSCRIPTION_TIERS`.
 */
export const teamUsageStatsSchema = z.object({
  tier: z.enum(["free", "growth", "enterprise"]),
  dailyResetsAt: z.string(),
  training: z.object({
    activeConcurrent: z.number(),
    maxConcurrent: z.number().nullable(),
    dailyCompletions: z.number(),
    dailyLimit: z.number().nullable(),
    monthlyCompletions: z.number(),
    monthlyLimit: z.number().nullable(),
  }),
  eval: z.object({
    activeConcurrent: z.number(),
    maxConcurrent: z.number().nullable(),
    dailySamplesUsed: z.number(),
    dailySamplesLimit: z.number().nullable(),
    monthlySamplesUsed: z.number(),
    monthlySamplesLimit: z.number().nullable(),
  }),
  deployment: z.object({
    activeCount: z.number(),
    maxActive: z.number().nullable(),
  }),
  halo: z.object({
    activeRuns: z.number(),
    maxConcurrentRuns: z.number().nullable(),
    dailyRunsUsed: z.number(),
    dailyRunsLimit: z.number().nullable(),
    monthlyRunsUsed: z.number(),
    monthlyRunsLimit: z.number().nullable(),
    dailyInferenceTokensUsed: z.number(),
    dailyInferenceSpendNanocredits: z.string(),
    monthlyInferenceTokensUsed: z.number(),
    monthlyInferenceSpendNanocredits: z.string(),
  }),
  proxiedInference: z.object({
    dailyRequestsUsed: z.number(),
    dailyRequestsLimit: z.number().nullable(),
    monthlyRequestsUsed: z.number(),
    monthlyRequestsLimit: z.number().nullable(),
    dailyDataBytesUsed: z.number(),
    dailyDataBytesLimit: z.number().nullable(),
    monthlyDataBytesUsed: z.number(),
    monthlyDataBytesLimit: z.number().nullable(),
  }),
  spans: z.object({
    dailySpansUsed: z.number(),
    dailySpansLimit: z.number().nullable(),
    monthlySpansUsed: z.number(),
    monthlySpansLimit: z.number().nullable(),
    dailyDataBytesUsed: z.number(),
    dailyDataBytesLimit: z.number().nullable(),
    monthlyDataBytesUsed: z.number(),
    monthlyDataBytesLimit: z.number().nullable(),
  }),
  signals: z.object({
    monthlySpanClassificationsUsed: z.number(),
    monthlySpanClassificationsIncluded: z.number(),
    monthlySpanClassificationsLimit: z.number().nullable(),
    monthlyTraceClassificationsUsed: z.number(),
    monthlyTraceClassificationsIncluded: z.number(),
    monthlyTraceClassificationsLimit: z.number().nullable(),
    monthlySessionClassificationsUsed: z.number(),
    monthlySessionClassificationsIncluded: z.number(),
    monthlySessionClassificationsLimit: z.number().nullable(),
  }),
});

/** ******************************************************************************
 *  Register User
 ******************************************************************************* */

export const registerUserParams = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  password: z.string(),
  confirmPassword: z.string(),
  turnstileToken: z.string().optional(),
});

export const registerUserRequest = serviceRequest.merge(
  z.object({
    params: registerUserParams,
  }),
);

export const registerUserResponse = authenticationResponse;

export type RegisterUserParams = z.infer<typeof registerUserParams>;
export type RegisterUserRequest = z.infer<typeof registerUserRequest>;
export type RegisterUserResponse = z.infer<typeof registerUserResponse>;

export type RegisterUserStoreResponse = { user: User; authToken: string };

export const registerTeamUserParams = z.object({
  _id: z.string(),
  email: z.string(),
  username: z.string().optional(),
  password: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  emailVerificationRequired: z.boolean(),
  emailVerifiedAt: z.date().nullish(),
  stripeCustomerId: z.string(),
  waitlistedAt: z.date().nullish(),
  registrationMethod: z.enum(USER_REGISTRATION_METHOD_SCHEMA),
  githubUsername: z.string().optional(),
  teamType: z.enum(TeamTypeSchema),
  registrationAllowanceUsdOverride: z.number().optional(),
});

export type RegisterUserTeamParams = z.infer<typeof registerTeamUserParams>;

/** ******************************************************************************
 *  Register User With OAuth
 ******************************************************************************* */

export const registerUserWithOAuthParams = z.object({
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  provider: OAuthProvider,
  githubUsername: z.string().optional(),
});

export type RegisterUserWithOAuthParams = z.infer<
  typeof registerUserWithOAuthParams
>;

export const registerUserWithOAuthRequest = serviceRequest.merge(
  z.object({
    params: registerUserWithOAuthParams,
  }),
);

export const registerUserWithOAuthResponse = authenticationResponse;

export type RegisterUserWithOAuthRequest = z.infer<
  typeof registerUserWithOAuthRequest
>;

export type RegisterUserWithOAuthResponse = z.infer<
  typeof registerUserWithOAuthResponse
>;
/** ******************************************************************************
 *  Set Retain Inference Results
 ******************************************************************************* */

export const setRetainInferenceResultsParams = z.object({
  retainInferenceResults: z.boolean(),
});

/**
 * Who changed a team's retention flag and through which entry point. Every
 * change is recorded with this in `team_retention_events`.
 */
export type TeamRetentionChangeActor = {
  /** The session user; under impersonation, the impersonated user. */
  userId: string;
  /** The staff user behind an impersonated session; null otherwise. */
  impersonatedByUserId: string | null;
  teamAccessMode: AuthTeamAccessMode;
  /** The entry point, e.g. `relay:team.setRetainInferenceResults`. */
  source: string;
};

/** ******************************************************************************
 *  Service Interface
 ******************************************************************************* */

/** ******************************************************************************
 *  Initialize Organization (for Better Auth)
 ******************************************************************************* */

export const initializeOrganizationParams = z.object({
  userId: z.string(),
  teamId: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
});

export type InitializeOrganizationParams = z.infer<
  typeof initializeOrganizationParams
>;

/** ******************************************************************************
 *  Create Organization
 ******************************************************************************* */

export const createOrganizationParams = z.object({
  userId: z.string(),
  organizationName: z.string(),
  organizationSlug: z.string().optional(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
});

export type CreateOrganizationParams = z.infer<typeof createOrganizationParams>;

// Frontend-facing params for creating organization (only needs org name)
export const createOrganizationInputParams = z.object({
  organizationName: z.string().min(1, "Organization name is required"),
  organizationSlug: z.string().optional(),
});

export type CreateOrganizationInputParams = z.infer<
  typeof createOrganizationInputParams
>;

export const checkOrganizationSlugAvailabilityParams = z.object({
  excludeOrganizationId: z.string().optional(),
  organizationSlug: z.string(),
});

export type CheckOrganizationSlugAvailabilityParams = z.infer<
  typeof checkOrganizationSlugAvailabilityParams
>;

export type OrganizationSlugAvailability = {
  available: boolean;
  reason: string | null;
  slug: string;
};

/**
 * A single team member with user identity + membership role, for super-admin
 * tooling (the Members tab). `signedUpAt` is the user's account creation date;
 * `joinedAt` is when they joined this team via Better Auth membership.
 */
export type TeamMemberAdminSummary = {
  membershipId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  joinedAt: Date;
  signedUpAt: Date;
};

export type TeamServiceInterface = {
  getTeam: (teamId: string) => Promise<Team | null>;
  getTeamCached: (teamId: string) => Promise<Team | null>;
  getRetainInferenceResultsCached: (teamId: string) => Promise<boolean>;

  getTeamOwner: (teamId: string) => Promise<User | undefined>;

  setRetainInferenceResults: (options: {
    retainInferenceResults: boolean;
    teamId?: string;
    actor: TeamRetentionChangeActor;
  }) => Promise<ServiceResponse>;

  /**
   * Initialize an organization after it's created via Better Auth.
   * This creates:
   * - Stripe customer
   * - Initial API key
   * - Initial balance deposit
   */
  initializeOrganization: (
    params: InitializeOrganizationParams,
  ) => Promise<void>;

  /**
   * Create a new organization for an existing user.
   * This creates:
   * - Team record
   * - Member record (with OWNER role)
   * - Default project
   * - Stripe customer
   * - Initial API key
   * - Initial balance deposit
   */
  createOrganization: (
    params: CreateOrganizationParams & {
      role?: string | null;
      emailVerified?: boolean | null;
    },
  ) => Promise<{
    organizationId: string;
    project: { createdAt: Date; id: string; name: string };
    slug: string;
  }>;

  checkOrganizationSlugAvailability: (
    params: CheckOrganizationSlugAvailabilityParams,
  ) => Promise<OrganizationSlugAvailability>;

  /**
   * Mark an organization as onboarded. Payment and phone verification do not
   * block completion (INF-4858); skip-SMS and no-card teams still finish.
   */
  completeOnboarding: (teamId: string) => Promise<ServiceResponse>;

  /**
   * After a successful SMS OTP: claim the number on this team and grant the
   * SMS verification credit when the flag is on. Does not enroll PAYG.
   */
  applySuccessfulSmsVerification: (input: {
    teamId: string;
    userId: string;
    phoneNumber: string;
  }) => Promise<ApplySuccessfulSmsVerificationResult>;

  applySuccessfulSmsVerificationForUser: (input: {
    teamId: string;
    userId: string;
  }) => Promise<ApplySuccessfulSmsVerificationResult>;

  isPhoneClaimedByOtherTeam: (input: {
    teamId: string;
    phoneNumber: string;
  }) => Promise<boolean>;

  /**
   * Sticky integration stamp. True when this call wrote `integrated_at`.
   */
  markTeamIntegratedIfUnset: (teamId: string) => Promise<boolean>;

  /**
   * Super-admin: reset onboarding for one of a user's teams back to "fresh".
   * Team-scoped only — nulls `teams.onboarded_at` and the pending onboarding
   * checkout columns, so the whole team (every member) re-enters onboarding.
   * Validates that `userId` is a member of `teamId` before writing and
   * audit-logs the acting admin; returns NOT_FOUND when the membership does not
   * exist. This is the sole public reset entry point; the destructive clears are
   * not exposed unguarded.
   */
  resetUserOnboarding: (params: {
    actingUserId: string;
    userId: string;
    teamId: string;
  }) => Promise<ServiceResponse>;

  /**
   * A user's teams with role + onboarding status, no project fan-out. Backs the
   * super-admin user-detail page and the reset membership gate.
   */
  listUserTeamsForAdmin: (params: { userId: string }) => Promise<
    {
      id: string;
      name: string;
      slug: string;
      role: string;
      memberSince: Date;
      onboardedAt: Date | null;
      stripeCustomerId: string | null;
    }[]
  >;

  /**
   * Search teams by name or slug, one capped page, ordered by the requested
   * column. Super-admin only; access is gated at the tRPC layer.
   */
  searchTeams: (params: {
    query: string;
    pageSize?: number;
    offset?: number;
    sort?: TeamSearchSort | null;
  }) => Promise<{ createdAt: Date; id: string; name: string; slug: string }[]>;

  /**
   * List all members of a team with user identity + membership role, for
   * super-admin tooling. Joins Better Auth membership (`better_auth_member`,
   * `organization_id = teamId`) to users; ordered owners-first then by join
   * date. Caller access is gated at the tRPC layer (`superUserProcedure`).
   */
  getTeamMembers: (params: {
    teamId: string;
  }) => Promise<TeamMemberAdminSummary[]>;
};

/** ******************************************************************************
 *  Storage Interface
 ******************************************************************************* */

export type TeamStorageInterface = {
  getTeam: (teamId: string) => Promise<Team | null>;

  /**
   * Registering users must go through the team service
   * to ensure that the user is associated with a team and
   * that the team has a valid role for the user (Owner)
   */
  registerUser: (
    registerUserParams: RegisterUserTeamParams,
  ) => Promise<RegisterUserStoreResponse>;

  getTeamOwner: (teamId: string) => Promise<User | undefined>;

  updateById: (teamId: string, params: Partial<Team>) => Promise<void>;

  /**
   * Set the onboarding timestamp only when it is still unset. Returns false
   * when another request has already completed onboarding for the team.
   */
  markOnboardingCompleteIfUnset: (
    teamId: string,
    onboardedAt: Date,
  ) => Promise<boolean>;

  clearOnboardedAt: (teamId: string) => Promise<void>;

  markPaymentOnboardingExempt: (teamId: string) => Promise<void>;

  /**
   * Persist a globally unique team phone claim. Returns `claimed: true` when
   * this team now holds the number. `already_claimed_by_this_team` is the
   * replay no-op; `claimed_by_other_team` means another team owns it.
   */
  getVerifiedPhoneNumber: (userId: string) => Promise<string | null>;

  findTeamIdByClaimedPhoneNumber: (
    phoneNumber: string,
  ) => Promise<string | null>;

  claimVerifiedPhoneNumber: (input: {
    teamId: string;
    phoneNumber: string;
  }) => Promise<ClaimVerifiedPhoneNumberResult>;

  /**
   * Stamp `integrated_at` only when it is still unset. Returns true when this
   * call wrote the stamp.
   */
  markIntegratedAtIfUnset: (
    teamId: string,
    integratedAt: Date,
  ) => Promise<boolean>;

  /**
   * All members of a team: Better Auth membership (`better_auth_member`,
   * `organization_id = teamId`) joined to users. Ordered owners first, then
   * admins, then everyone else, stable by join date.
   */
  getTeamMembersWithUsers: (
    teamId: string,
  ) => Promise<TeamMemberAdminSummary[]>;
};
